import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { uploadImageApiAssetsImageUploadPost } from "../generated/api/sdk.gen";
import type { AssetResponse } from "../generated/api/types.gen";
import { createImageAssetForProject } from "../lib/asset-create";
import { useModalCommitGuard } from "./useModalCommitGuard";

interface UseImageUploadCommitArgs<S> {
  isOpen: boolean;
  /** The commit is dropped if the user left the image tab before it landed. */
  isImageTab: boolean;
  file: File | null;
  name: string;
  saveAsAsset: boolean;
  projectId?: string | null;
  /** Build the selection for an upload that was not saved to the library. */
  fromUpload: (url: string, name: string) => S;
  /** Build the selection for an upload that became a saved asset. */
  fromAsset: (asset: AssetResponse) => S;
  onCommit: (selection: S) => void;
}

/** Upload the picked image and hand back a selection.
 *
 *  An upload can complete after the user has closed the modal or switched
 *  tabs, so the success handler checks the latest state rather than the state
 *  captured when the mutation started. Closing and reopening counts as a new
 *  session (form state is reset), and reverts both flags — so each attempt
 *  also records which open-session it belongs to, and an attempt from an
 *  earlier session is dropped even when the flags match again.
 *
 *  The session id advances during render rather than in an effect: batched
 *  renders dedupe effect runs against the last *executed* dependencies, which
 *  can swallow a close→reopen transition entirely, while this reads as
 *  ground truth at whichever moment the network actually resolves. */
export function useImageUploadCommit<S>({
  isOpen,
  isImageTab,
  file,
  name,
  saveAsAsset,
  projectId,
  fromUpload,
  fromAsset,
  onCommit,
}: UseImageUploadCommitArgs<S>) {
  const queryClient = useQueryClient();
  const guard = useModalCommitGuard({ isOpen, isActiveTab: isImageTab });

  // Filing the new asset into the project can fail after the upload landed, so
  // a retry re-files that asset instead of uploading a second copy. Picking a
  // different file starts over.
  const created = useRef<AssetResponse | null>(null);
  const createdFrom = useRef<File | null>(null);
  if (createdFrom.current !== file) {
    createdFrom.current = file;
    created.current = null;
  }

  return useMutation({
    mutationFn: async (): Promise<S> => {
      guard.beginAttempt();
      if (!file) throw new Error("No file selected");
      const trimmed = name.trim();

      // An unsaved upload still needs a durable URL so the image can travel
      // with the refinement, but stays out of the library.
      if (!saveAsAsset) {
        const { data } = await uploadImageApiAssetsImageUploadPost({
          body: { file },
        });
        if (!data) throw new Error("Failed to upload image");
        return fromUpload(data.url, trimmed);
      }

      const asset = await createImageAssetForProject(queryClient, {
        file,
        name: trimmed,
        projectId,
        created,
      });
      return fromAsset(asset);
    },
    onSuccess: (selection) => {
      if (!guard.shouldCommit()) return;
      onCommit(selection);
    },
  });
}
